import {ChevronDownIcon, EllipsisHorizontalIcon} from "@heroicons/react/20/solid";
import {ReactNode} from "react";
import {OtsBrand, OtsColorSchemePreference, OtsThemeRoot} from "../theme/OtsTheme";
import {OtsPopover, OtsPopoverPlacement} from "./OtsPopover";
import {OtsTooltip} from "./OtsTooltip";

export type MetricTone = "success" | "warning" | "danger" | "neutral" | "primary";

/** A small, reusable value that can be displayed in a strip, card, footer, or toolbar. */
export type Metric = {
  id: string;
  label: ReactNode;
  value: ReactNode;
  icon?: ReactNode;
  tone?: MetricTone;
  hidden?: boolean;
  /** Lower values remain visible first when a strip reaches its maximum. */
  priority?: number;
  /** Short themed hover/focus help. Use popover for richer or interactive details. */
  tooltip?: ReactNode;
  /** Optional rich details opened from the metric itself. */
  popover?: ReactNode;
};

export type MetricStripProps = {
  metrics: Metric[];
  /** Optional context such as "All rows", "4 selected", or "Stored summary". */
  label?: ReactNode;
  /** Maximum number shown inline. Remaining metrics are available in the overflow popover. */
  maxVisible?: number;
  density?: "compact" | "default";
  /** Where the overflow popover opens; a strip in a footer opens it upward. */
  overflowPlacement?: OtsPopoverPlacement;
  brand?: OtsBrand;
  colorScheme?: OtsColorSchemePreference;
  className?: string;
  ariaLabel?: string;
};

const toneClasses: Record<MetricTone, string> = {
  success: "border-[var(--ots-success-outline)] bg-[var(--ots-success-container)] text-[var(--ots-on-success-container)]",
  warning: "border-[var(--ots-warning-outline)] bg-[var(--ots-warning-container)] text-[var(--ots-on-warning-container)]",
  danger: "border-[var(--ots-danger-outline)] bg-[var(--ots-danger-container)] text-[var(--ots-on-danger-container)]",
  neutral: "border-[var(--ots-outline-variant)] bg-[var(--ots-neutral-container)] text-[var(--ots-on-neutral-container)]",
  primary: "border-[var(--ots-outline)] bg-[var(--ots-primary-container)] text-[var(--ots-primary)]",
};

const metricClasses = (metric: Metric, density: "compact" | "default", interactive = false) => [
  "inline-flex items-center rounded-md border font-semibold",
  density === "compact" ? "gap-1 px-2 py-1 text-[11px]" : "gap-1.5 px-2.5 py-1.5 text-xs",
  toneClasses[metric.tone ?? "neutral"],
  interactive ? "cursor-pointer outline-none transition hover:brightness-95 focus-visible:ring-2 focus-visible:ring-[var(--ots-primary)] focus-visible:ring-offset-1" : "",
].join(" ");

const MetricContent = ({metric, showDisclosure = false}: {metric: Metric; showDisclosure?: boolean}) => <>
  {metric.icon && <span aria-hidden="true" className="size-3.5 shrink-0 [&>svg]:size-full">{metric.icon}</span>}
  <span className="font-medium opacity-80">{metric.label}</span>
  <span>{metric.value}</span>
  {showDisclosure && <ChevronDownIcon aria-hidden="true" className="size-3"/>}
</>;

export type MetricChipProps = {
  metric: Metric;
  density?: "compact" | "default";
  className?: string;
};

/** A reusable metric presentation with optional tooltip or rich popover details. */
export const MetricChip = ({metric, density = "default", className}: MetricChipProps) => {
  if (!metric.popover) {
    const chip = <span tabIndex={metric.tooltip != null ? 0 : undefined} className={[metricClasses(metric, density), className].filter(Boolean).join(" ")}>
      <MetricContent metric={metric}/>
    </span>;
    return metric.tooltip != null ? <OtsTooltip content={metric.tooltip} density={density}>{chip}</OtsTooltip> : chip;
  }

  return <OtsPopover
    density={density}
    tooltip={metric.tooltip}
    className={className}
    buttonClassName={metricClasses(metric, density, true)}
    trigger={<MetricContent metric={metric} showDisclosure/>}>
    {metric.popover}
  </OtsPopover>;
};

/** A context-neutral strip of metrics with priority-aware overflow. */
export const MetricStrip = ({
  metrics,
  label,
  maxVisible = 3,
  density = "default",
  overflowPlacement = "bottom end",
  brand,
  colorScheme,
  className,
  ariaLabel = "Metrics",
}: MetricStripProps) => {
  const visible = metrics
    .map((metric, index) => ({metric, index}))
    .filter(({metric}) => !metric.hidden)
    .sort((left, right) => (left.metric.priority ?? Number.MAX_SAFE_INTEGER) - (right.metric.priority ?? Number.MAX_SAFE_INTEGER) || left.index - right.index)
    .map(({metric}) => metric);
  const inlineCount = Number.isFinite(maxVisible) ? Math.max(1, Math.floor(maxVisible)) : visible.length;
  const inlineMetrics = visible.slice(0, inlineCount);
  const overflowMetrics = visible.slice(inlineCount);

  return <OtsThemeRoot brand={brand} colorScheme={colorScheme} className={className} data-testid="metric-strip">
    <div className="flex min-w-0 flex-wrap items-center gap-1.5" aria-live="polite" aria-label={ariaLabel}>
      {label != null && <span className="mr-0.5 text-[10px] font-bold uppercase tracking-wide text-[var(--ots-text-muted)]">{label}</span>}
      {inlineMetrics.map(metric => <MetricChip key={metric.id} metric={metric} density={density}/>)}
      {!!overflowMetrics.length && <OtsPopover
        density={density}
        placement={overflowPlacement}
        ariaLabel={`Show ${overflowMetrics.length} more metrics`}
        buttonClassName={[
            "inline-flex items-center rounded-md border border-[var(--ots-outline-variant)] bg-[var(--ots-surface)] font-semibold text-[var(--ots-text-muted)] outline-none transition hover:bg-[var(--ots-surface-variant)] focus-visible:ring-2 focus-visible:ring-[var(--ots-primary)]",
            density === "compact" ? "gap-1 px-2 py-1 text-[11px]" : "gap-1.5 px-2.5 py-1.5 text-xs",
          ].join(" ")}
        panelClassName="flex flex-col items-start gap-1.5"
        trigger={<>
          <EllipsisHorizontalIcon aria-hidden="true" className="size-3.5"/>
          +{overflowMetrics.length}
        </>}>
        {overflowMetrics.map(metric => <MetricChip key={metric.id} metric={metric} density={density}/>)}
      </OtsPopover>}
    </div>
  </OtsThemeRoot>;
};
